import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import LogoutModal from '../admin/LogoutModal';
import { authService } from '../../services/api';

const DoctorHeader = ({ doctorData, brand, primaryColor = '#06B6D4' }) => {
    const navigate = useNavigate();
    const { slug: routeSlug } = useParams();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [showLogoutToast, setShowLogoutToast] = useState(false);

    const API_BASE_URL =
        process.env.REACT_APP_API_BASE_URL?.replace('/api', '') ||
        'http://localhost:8000';

    const displayName = doctorData?.name || doctorData?.dokter?.nama_dokter || 'Dokter';
    const specialization = doctorData?.dokter?.spesialisasi || 'Dokter Umum';

    const clinicName = brand?.nama_klinik;
    const fallbackSlug =
        routeSlug ||
        brand?.slug ||
        doctorData?.dokter?.klinik?.slug ||
        doctorData?.klinik?.slug ||
        (() => {
            try {
                const u = JSON.parse(localStorage.getItem('user') || '{}');
                return u?.dokter?.klinik?.slug || u?.klinik?.slug || null;
            } catch {
                return null;
            }
        })();

    const handleLogout = async () => {
        setIsLoggingOut(true);
        let redirectSlug = fallbackSlug;
        try {
            const response = await authService.logout();
            redirectSlug = response?.data?.clinic_slug || redirectSlug;
        } catch (error) {
            // Even when API logout fails, clear local session to prevent stale auth state.
            redirectSlug = fallbackSlug;
        } finally {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            sessionStorage.clear();
            setIsLoggingOut(false);
            setIsLogoutModalOpen(false);
            setShowLogoutToast(true);
            setTimeout(() => {
                navigate(redirectSlug ? `/${redirectSlug}/login` : '/login', { replace: true });
            }, 500);
        }
    };

    const logoSrc = (() => {
        const u = brand?.logo_url;
        if (!u) return '/careline-logo.svg';
        if (u.startsWith('http')) return u;
        return `${API_BASE_URL}${u.startsWith('/') ? '' : '/'}${u}`;
    })();

    const doctorPhotoSrc = (() => {
        const raw =
            doctorData?.dokter?.foto ||
            doctorData?.foto ||
            null;
        if (!raw) return 'https://i.pravatar.cc/150?u=doctor-default';
        if (raw.startsWith('http') || raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
        return `${API_BASE_URL}${raw.startsWith('/') ? '' : '/'}${raw}`;
    })();

    return (
        <header
            className="flex justify-between items-center px-10 py-3 bg-white border-b border-gray-50 fixed top-0 w-full z-50"
            style={{ '--clinic-primary': primaryColor }}
        >
            <div className="flex items-center gap-3 min-w-0">
                <img src={logoSrc} alt={clinicName || 'Klinik'} className="h-10 w-auto max-w-[200px] object-contain shrink-0" />
                {clinicName && (
                    <span
                        className="text-sm font-bold tracking-tight truncate max-w-[min(40vw,280px)]"
                        style={{ color: primaryColor }}
                    >
                        {clinicName}
                    </span>
                )}
            </div>

            <div className="relative">
                <div
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center gap-4 cursor-pointer hover:bg-gray-50 px-4 py-2 rounded-2xl transition-all group"
                >
                    <img
                        src={doctorPhotoSrc}
                        alt={displayName}
                        className="w-12 h-12 rounded-full border-2 border-white shadow-sm object-cover"
                        onError={(e) => {
                            e.currentTarget.src = 'https://i.pravatar.cc/150?u=doctor-fallback';
                        }}
                    />
                    <div className="text-right">
                        <p className="text-sm font-bold text-gray-800 group-hover:text-[color:var(--clinic-primary)] transition-colors">{displayName}</p>
                        <p className="text-xs text-gray-400 font-medium">{specialization}</p>
                    </div>
                    <Icon
                        icon="material-symbols:keyboard-arrow-down"
                        className={`text-gray-400 group-hover:text-[color:var(--clinic-primary)] transition-all ${isDropdownOpen ? 'rotate-180' : ''}`}
                    />
                </div>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                    <>
                        <div
                            className="fixed inset-0 z-10"
                            onClick={() => setIsDropdownOpen(false)}
                        />
                        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 rounded-2xl shadow-xl z-20 p-2 animate-in fade-in zoom-in-95 duration-200">
                            <button
                                onClick={() => {
                                    setIsDropdownOpen(false);
                                    setIsLogoutModalOpen(true);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                            >
                                <Icon icon="material-symbols:logout-rounded" className="text-lg" />
                                <span className="font-bold">Keluar</span>
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Logout Modal */}
            <LogoutModal
                isOpen={isLogoutModalOpen}
                onClose={() => setIsLogoutModalOpen(false)}
                onConfirm={handleLogout}
                isProcessing={isLoggingOut}
            />

            {showLogoutToast && (
                <div className="fixed top-5 right-5 z-[120] rounded-xl bg-emerald-500 text-white px-4 py-2 shadow-lg text-sm font-semibold">
                    Berhasil logout...
                </div>
            )}
        </header>
    );
};

export default DoctorHeader;
